import { useContext, useEffect, useRef } from "react";
import AnalyticsContext from "app/utils/analytics/AnalyticsContext";
import { buildCommonData, buildTagData } from "app/utils/analytics/buildTagData";
import { useLocation } from "react-router-dom";
import { datadogRum } from "@datadog/browser-rum";
import isEmpty from "lodash/isEmpty";
import { normalizeAnalyticsTagData } from "app/utils/analytics/normalizeAnalyticsTagData";
import Cookies from "js-cookie";
import { IS_PRODUCTION } from "app/constants";
import axios from "axios";

const dataLayer = typeof window !== "undefined" ? window.dataLayer : [];

const sendDataToAnalyticsEndPoint = ({ env, data }) => {
	// @see https://console.aws.amazon.com/s3/buckets/datalayer.perfectstay.io pour voir les infos envoyés (refrech toutes les 10 minutes)

	const newData = {
		...data,
		originalEvent: data.event,
	};

	const analyticsEndPoint = env?.includes("live")
		? "https://x6pc9ierpi.execute-api.eu-west-1.amazonaws.com/live"
		: "https://datalayer.test.analytics.perfectstay.io";

	if (IS_PRODUCTION) {
		axios
			.post(analyticsEndPoint, JSON.stringify(data), {
				headers: { "Content-type": "text/plain" },
			})
			.catch(() => {
				newData.event = "send_tag_big_query_error";
				if (dataLayer) {
					dataLayer.push(newData);
				}
			});
	}
};

export const useAnalytics = () => {
	const { store = {} } = useContext(AnalyticsContext);
	const { pathname, search } = useLocation();

	// La location est lue au moment du track, pas à celui du rendu : un consommateur qui
	// mémoïse son handler avec des deps vides conserve le premier `track`, et sans ce ref
	// il rapporterait la page du premier rendu pour le reste de la session.
	// L'objet rendu par useRef étant stable, la closure figée y lit la valeur à jour.
	const locationRef = useRef({ pathname, search });

	useEffect(() => {
		locationRef.current = { pathname, search };
	}, [pathname, search]);

	return {
		track: (eventName, payload = {}) => {
			if (dataLayer) {
				const state = store.getState() || {};

				const commonData = buildCommonData({ state, ...locationRef.current });
				const { env } = commonData;

				const tagData = buildTagData({ eventName, payload, commonData });

				// client id GA : il fait partie du tag, il est donc lisible dans le dataLayer et
				// dans Datadog, et pas seulement dans le POST vers l'endpoint analytics
				tagData._ga = Cookies.get("_ga");

				if (!isEmpty(payload)) {
					tagData.eventProperties = payload;
				}

				const normalizedTagData = normalizeAnalyticsTagData(tagData);

				dataLayer.push(normalizedTagData);

				sendDataToAnalyticsEndPoint({ data: normalizedTagData, env });

				if (env?.includes("live")) {
					const { event, ...restTagData } = normalizedTagData;

					datadogRum.addAction(event, {
						...restTagData,
					});
				}
			}
		},
	};
};
